import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Link,
  Chip,
  Stack,
} from '@mui/material';
import type { Pool } from '../../types/domain';
import CameraLayoutSimulator from '../../components/pool/CameraLayoutSimulator';
import { openPoolManagementPopupWindow } from '../../components/monitoring/cctvPopupRoutes';

const STORAGE_KEY = 'kyma.cameraLayoutPlanner.poolId';

/** POOL001, POOL002 … 코드 순 정렬 */
function comparePoolCode(a: string, b: string): number {
  return a.localeCompare(b, 'en', { numeric: true, sensitivity: 'base' });
}

function poolOptionLabel(pool: Pool): string {
  const code = String(pool.code || '').trim();
  const name = String(pool.name || '').trim();
  if (code && name) return `${code} (${name})`;
  return code || name || '—';
}

type Props = {
  pools: Pool[];
  loading?: boolean;
};

/**
 * 관제센터 전용 카메라 설치·배치 작업 화면.
 * PoolSettings 의 「카메라 배치」 탭과 동일한 시뮬레이터를 사용하되, 시설 선택 UI만 별도 제공.
 */
const CameraLayoutPlannerPage: React.FC<Props> = ({ pools, loading = false }) => {
  const [poolId, setPoolId] = useState<number | ''>('');

  const sortedPools = useMemo(
    () =>
      [...pools].sort((a, b) =>
        comparePoolCode(String(a.code || ''), String(b.code || '')),
      ),
    [pools],
  );

  useEffect(() => {
    if (!sortedPools.length) {
      setPoolId('');
      return;
    }
    const ids = sortedPools.map((p) => Number(p.id)).filter((id) => !Number.isNaN(id));
    const stored = sessionStorage.getItem(STORAGE_KEY);
    const storedNum = stored ? Number(stored) : NaN;
    if (stored && ids.includes(storedNum)) {
      setPoolId(storedNum);
      return;
    }
    const first = Number(sortedPools[0].id);
    if (!Number.isNaN(first)) setPoolId(first);
  }, [sortedPools]);

  const handlePoolChange = (e: SelectChangeEvent<number | ''>) => {
    const v = e.target.value;
    if (v === '') {
      setPoolId('');
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    const n = Number(v);
    setPoolId(n);
    sessionStorage.setItem(STORAGE_KEY, String(n));
  };

  const selectedPool = sortedPools.find((p) => Number(p.id) === poolId);

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
        카메라 배치·설치
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        시설을 선택한 뒤 천장/벽 카메라 위치·높이·하향각·화각을 설계. 저장은 선택한
        시설 단위로만 적용됨
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 280 }} disabled={loading || !sortedPools.length}>
          <InputLabel id="camera-planner-pool-label">작업 시설</InputLabel>
          <Select
            labelId="camera-planner-pool-label"
            label="작업 시설"
            value={poolId}
            onChange={handlePoolChange}
          >
            {sortedPools.map((pool) => {
              const id = Number(pool.id);
              if (Number.isNaN(id)) return null;
              return (
                <MenuItem key={id} value={id}>
                  {poolOptionLabel(pool)}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        {selectedPool && (
          <Chip
            size="small"
            variant="outlined"
            label={`저장 대상: ${selectedPool.code || selectedPool.name}`}
          />
        )}
        {selectedPool?.code && (
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => openPoolManagementPopupWindow(selectedPool.code)}
            sx={{ ml: { sm: 'auto' }, cursor: 'pointer', border: 'none', background: 'none' }}
          >
            시설 관리
          </Link>
        )}
      </Stack>

      {loading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          시설 목록을 불러오는 중…
        </Alert>
      )}

      {!loading && !sortedPools.length && (
        <Alert severity="warning">
          등록된 시설 없음. 「시설 관리」에서 시설을 먼저 등록.
        </Alert>
      )}

      {poolId !== '' && !Number.isNaN(Number(poolId)) && (
        <CameraLayoutSimulator
          key={poolId}
          poolId={Number(poolId)}
          poolName={selectedPool?.name}
        />
      )}
    </Box>
  );
};

export default CameraLayoutPlannerPage;
